/**
 * KRA Photo Journal – PublicGallery
 *
 * Customer-facing read-only album view. Rendered when URL contains
 * `?share=<token>`. No login, no admin, no download — just view.
 *
 * Photos are served via Drive thumbnail CDN URLs (not the worker download
 * proxy). Right-click, drag, and long-press are disabled to discourage
 * casual save. This is social copy-protection, not real DRM.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchPublicShare, type GalleryFile, type SharePublicResponse } from "../lib/api.js";

interface Props {
  token: string;
}

function driveThumbnailUrl(fileId: string, size: "thumb" | "full"): string {
  const sz = size === "thumb" ? "w600" : "w1600";
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=${sz}`;
}

/** Worker download proxy URL — 2048px, EXIF stripped, Content-Disposition: attachment. */
function driveDownloadUrl(fileId: string): string {
  return `/api/drive/${encodeURIComponent(fileId)}/download`;
}

/** Save a file via fetch → blob → <a download> so the browser triggers a real save. */
async function downloadFile(fileId: string, name: string): Promise<void> {
  const resp = await fetch(driveDownloadUrl(fileId));
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name || `photo_${fileId.slice(-8)}.jpg`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function formatRemaining(expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "Đã hết hạn";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `Còn ${minutes} phút`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Còn ${hours} giờ`;
  const days = Math.floor(hours / 24);
  return `Còn ${days} ngày`;
}

export function PublicGallery({ token }: Props) {
  const [data, setData] = useState<SharePublicResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; expired: boolean } | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const allowDownload = data?.allowDownload === true;

  // When downloads are NOT allowed, discourage casual save via global handlers.
  // When the staff explicitly enabled downloads, leave the browser's native
  // right-click / drag behavior intact so customers can save normally.
  useEffect(() => {
    if (allowDownload) return;
    const prevent = (e: Event) => e.preventDefault();
    document.addEventListener("contextmenu", prevent);
    document.addEventListener("dragstart", prevent);
    return () => {
      document.removeEventListener("contextmenu", prevent);
      document.removeEventListener("dragstart", prevent);
    };
  }, [allowDownload]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPublicShare(token)
      .then((res) => {
        if (!cancelled) {
          setData(res);
          setError(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const code = (err as { code?: string }).code;
        const expired = code === "SHARE_EXPIRED" || code === "SHARE_NOT_FOUND";
        const message = expired
          ? "Link đã hết hạn hoặc không tồn tại"
          : (err instanceof Error ? err.message : "Không tải được bộ ảnh");
        setError({ message, expired });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prev = useCallback(() => {
    setLightboxIndex((i) => (i === null || !data ? null : Math.max(0, i - 1)));
  }, [data]);
  const next = useCallback(() => {
    setLightboxIndex((i) => (i === null || !data ? null : Math.min(data.files.length - 1, i + 1)));
  }, [data]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, closeLightbox, prev, next]);

  const remaining = useMemo(() => data ? formatRemaining(data.expiresAt) : "", [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0e0d0b] flex items-center justify-center">
        <div className="text-white/40 text-sm animate-pulse">Đang tải bộ ảnh…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0e0d0b] flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <div className="text-white/80 text-lg font-medium mb-2">
            {error.expired ? "Link đã hết hạn" : "Không tải được bộ ảnh"}
          </div>
          <p className="text-white/40 text-sm">
            {error.expired
              ? "Vui lòng liên hệ studio để được gửi lại link mới."
              : error.message}
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className={["min-h-screen bg-[#0e0d0b]", allowDownload ? "" : "select-none"].join(" ")}>
      {/* Header */}
      <div className="sticky top-0 z-10 backdrop-blur-md bg-[#0e0d0b]/85 border-b border-white/8">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-white text-base sm:text-lg font-semibold truncate">{data.title}</h1>
            <p className="text-white/40 text-xs mt-0.5">{data.total} ảnh · {remaining}</p>
          </div>
          <div className="text-white/30 text-[10px] uppercase tracking-wider hidden sm:block">
            Kimono Rental Ann
          </div>
        </div>
      </div>

      {/* Masonry grid — preserves each photo's natural aspect ratio */}
      <main className="max-w-6xl mx-auto p-3 sm:p-5">
        <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-2 sm:gap-3 [column-fill:_balance]">
          {data.files.map((file, i) => (
            <div
              key={file.fileId}
              className="mb-2 sm:mb-3 block w-full break-inside-avoid overflow-hidden rounded-lg bg-white/5 group relative"
            >
              <button
                onClick={() => setLightboxIndex(i)}
                className="block w-full"
              >
                <img
                  src={driveThumbnailUrl(file.fileId, "thumb")}
                  alt=""
                  loading="lazy"
                  draggable={allowDownload}
                  {...(allowDownload ? {} : { onContextMenu: (e: React.MouseEvent) => e.preventDefault() })}
                  className="block w-full h-auto transition-transform duration-300 group-hover:scale-[1.03]"
                />
              </button>
              {allowDownload && (
                <button
                  onClick={(e) => { e.stopPropagation(); downloadFile(file.fileId, file.name); }}
                  className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover:opacity-100 transition"
                  aria-label="Tải ảnh về"
                  title="Tải ảnh về"
                >
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>

        {data.files.length === 0 && (
          <div className="text-center text-white/40 text-sm py-20">
            Bộ ảnh hiện chưa có ảnh nào.
          </div>
        )}

        <footer className="text-center text-white/25 text-[10px] mt-12 mb-6">
          © Kimono Rental Ann{allowDownload ? "" : " · Vui lòng không tải / chia sẻ lại"}
        </footer>
      </main>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && data.files[lightboxIndex] && (
          <Lightbox
            file={data.files[lightboxIndex]!}
            index={lightboxIndex}
            total={data.files.length}
            allowDownload={allowDownload}
            onClose={closeLightbox}
            onPrev={prev}
            onNext={next}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function Lightbox({
  file, index, total, allowDownload, onClose, onPrev, onNext,
}: {
  file: GalleryFile;
  index: number;
  total: number;
  allowDownload: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (saving) return;
    setSaving(true);
    try {
      await downloadFile(file.fileId, file.name);
    } catch {
      // ignore; user can retry
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-[80] bg-black/95"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="fixed inset-0 z-[81] flex flex-col"
        onClick={onClose}
      >
        <div className="flex items-center justify-between px-5 py-4 text-white/80 text-xs">
          <span>{index + 1} / {total}</span>
          <div className="flex items-center gap-2">
            {allowDownload && (
              <button
                onClick={handleDownload}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 h-9 rounded-full bg-white/10 hover:bg-white/20 transition disabled:opacity-50"
                aria-label="Tải ảnh"
                title="Tải ảnh về"
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <span>{saving ? "Đang tải…" : "Tải"}</span>
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition"
              aria-label="Đóng"
            >
              <svg width={13} height={13} viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <path d="M1 1l11 11M12 1L1 12"/>
              </svg>
            </button>
          </div>
        </div>

        <div
          className="flex-1 min-h-0 flex items-center justify-center px-4 pb-4"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={`https://drive.google.com/thumbnail?id=${file.fileId}&sz=w1600`}
            alt=""
            draggable={allowDownload}
            {...(allowDownload ? {} : { onContextMenu: (e: React.MouseEvent) => e.preventDefault() })}
            className={["w-full h-full object-contain", allowDownload ? "" : "select-none"].join(" ")}
          />
        </div>

        {index > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            aria-label="Trước"
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
        )}
        {index < total - 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            aria-label="Sau"
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        )}
      </motion.div>
    </>
  );
}
